/**
 * نمونه‌ی `axios` و interceptor‌ها. تنها جایی که با شبکه تماس گرفته می‌شود.
 *
 * چرا interceptor و نه پوشش دست‌ساز: افزودن توکن، نگاشت خطا و timeout سه چیزی
 * هستند که در هر درخواست لازم‌اند. با interceptor یک بار تعریف می‌شوند؛ با
 * پوشش دست‌ساز، هر تابع API باید یادش باشد آن‌ها را صدا بزند و روزی یکی
 * فراموش می‌شود.
 */

import axios, { type AxiosError, type AxiosInstance } from 'axios'

import { apiBase, slugFromPath } from '@/lib/base-path'

/** کلید نگه‌داشتن توکن نشست. */
const TOKEN_KEY = 'qatran.session.token'

/**
 * `sessionStorage` نه `localStorage`.
 *
 * توکن با بستن تب از بین می‌رود. این عمدی است: نسخه‌ی مشتری روی ماشین‌های
 * مشترک هیئت‌مدیره باز می‌شود و نشست ماندگار یعنی نفر بعدی بدون رمز وارد
 * پایگاه دانش محرمانه شود.
 */
const store = (): Storage | null => {
  try {
    return window.sessionStorage
  } catch {
    // در حالت حریم خصوصی سخت‌گیرانه، دسترسی به storage استثنا می‌دهد. در آن
    // حالت توکن فقط در حافظه می‌ماند و با بارگذاری دوباره از بین می‌رود.
    return null
  }
}

let memoryToken = ''

export const getToken = (): string => store()?.getItem(TOKEN_KEY) ?? memoryToken

export const setToken = (token: string): void => {
  memoryToken = token
  store()?.setItem(TOKEN_KEY, token)
}

export const clearToken = (): void => {
  memoryToken = ''
  store()?.removeItem(TOKEN_KEY)
}

/**
 * کلید هویت مشتری — پیشوند. کلید نهایی به مسیر عمومی نسخه گره می‌خورد.
 *
 * گره‌خوردن به `slug` لازم است: یک مرورگر می‌تواند به چند دستیار منتشرشده وصل
 * شود و هویت مشترک، آرشیو دو دستیار را به هم می‌چسباند.
 */
const CUSTOMER_KEY_PREFIX = 'qatran.customer.key.'

/**
 * ⚠️ این یکی `localStorage` است، برخلاف توکن نشست.
 *
 * دلیل: این کلید **هویت** است، نه نشست. با بستن تب باید بماند، وگرنه کاربر در
 * هر بازگشت یک هویت تازه می‌گرفت و آرشیو تصمیم‌های قبلی‌اش را از دست می‌داد؛
 * یعنی همان قابلیتی که این تغییر می‌خواست بسازد از بین می‌رفت.
 *
 * پیامدی که باید دانسته باشد: روی یک مرورگر مشترک، نفر بعدی که رمز همان نسخه
 * را دارد هویت قبلی را به ارث می‌برد. این از وضعیت قبلی — که همه‌ی مشتریان یک
 * لینک، آرشیو یکدیگر را می‌دیدند — به‌مراتب محدودتر است، اما جای هویت واقعی
 * کاربر را نمی‌گیرد.
 */
const identityStore = (): Storage | null => {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

const customerKeys = new Map<string, string>()

const customerKeyName = (slug: string): string => `${CUSTOMER_KEY_PREFIX}${slug}`

export const getCustomerKey = (slug: string): string =>
  identityStore()?.getItem(customerKeyName(slug)) ?? customerKeys.get(slug) ?? ''

export const setCustomerKey = (slug: string, key: string): void => {
  if (key === '') return
  customerKeys.set(slug, key)
  identityStore()?.setItem(customerKeyName(slug), key)
}

/**
 * خطای رو به کاربر.
 *
 * `message` همیشه فارسی است و مستقیماً از backend می‌آید؛ فرانت‌اند هیچ متن
 * خطایی نمی‌سازد. `code` شناسه‌ی ماشین‌خوان است تا واکنش رفتاری به یک حالت
 * خاص، به تطبیق روی متن فارسی وابسته نباشد — تطبیق روی متن با اولین ویرایش
 * پیام می‌شکند.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly fields: string[]
  readonly technicalDetail: string

  constructor(init: {
    message: string
    status: number
    code?: string
    fields?: string[]
    technicalDetail?: string
  }) {
    super(init.message)
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code ?? ''
    this.fields = init.fields ?? []
    this.technicalDetail = init.technicalDetail ?? ''
  }

  /** نشست منقضی یا رمز لازم است. */
  get isUnauthorized(): boolean {
    return this.status === 401
  }

  /** این عمل در حالت اجرای جاری مجاز نیست. */
  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  /** پیش‌شرطی برآورده نشده — مثل «اول انتشار کن». */
  get isConflict(): boolean {
    return this.status === 409
  }
}

const NETWORK_MESSAGE =
  'ارتباط با سرویس برقرار نشد. مطمئن شوید سرویس در حال اجراست و دوباره تلاش کنید.'

const TIMEOUT_MESSAGE =
  'پاسخ سرویس در زمان مورد انتظار نرسید. اگر مشاوره سنگین بوده، دوباره تلاش کنید.'

const UNKNOWN_MESSAGE = 'خطای ناشناخته‌ای رخ داد.'

/** شکل نرمال‌شده‌ی بدنه‌ی خطا. همه‌ی فیلدها الزامی‌اند تا مصرفش شرط نخواهد. */
interface ErrorDetail {
  message: string
  code: string
  fields: string[]
  technicalDetail: string
}

const asString = (value: unknown): string => (typeof value === 'string' ? value : '')

/**
 * بدنه‌ی خطا را با احتیاط می‌خواند و نرمال می‌کند.
 *
 * اعتماد کور به شکل بدنه یعنی اگر روزی یک proxy میانی صفحه‌ی HTML خطا
 * برگرداند، رابط با «undefined» بشکند. پس هر فیلد بررسی نوع می‌شود و در نبود
 * شکل مورد انتظار، پیام عمومی جایش می‌نشیند.
 */
const readDetail = (data: unknown): ErrorDetail => {
  const fallback: ErrorDetail = {
    message: UNKNOWN_MESSAGE,
    code: '',
    fields: [],
    technicalDetail: '',
  }
  if (typeof data !== 'object' || data === null || !('detail' in data)) {
    return fallback
  }
  const { detail } = data
  if (typeof detail !== 'object' || detail === null) {
    return fallback
  }
  const record: Record<string, unknown> = { ...detail }
  const rawFields = record.fields
  return {
    message: asString(record.message) === '' ? UNKNOWN_MESSAGE : asString(record.message),
    code: asString(record.code),
    fields: Array.isArray(rawFields)
      ? rawFields.filter((item): item is string => typeof item === 'string')
      : [],
    technicalDetail: asString(record.technical_detail),
  }
}

const toApiError = (error: AxiosError): ApiError => {
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return new ApiError({ message: TIMEOUT_MESSAGE, status: 0, code: 'Timeout' })
  }
  if (error.response === undefined) {
    return new ApiError({ message: NETWORK_MESSAGE, status: 0, code: 'NetworkError' })
  }

  const detail = readDetail(error.response.data)
  return new ApiError({
    message: detail.message,
    status: error.response.status,
    code: detail.code,
    fields: detail.fields,
    technicalDetail: detail.technicalDetail,
  })
}

/**
 * پایه‌ی آدرس API.
 *
 * ترتیب: `VITE_API_BASE_URL` اگر تنظیم شده باشد (استقرار جدا از هم)، وگرنه
 * **مسیر پایه‌ی سرویس**. مسیر پایه در نسخه‌ی مشتریِ زیرمسیری اهمیت دارد: اگر
 * اپ روی `assistant.example.com/acme` سرو شود، درخواست باید به
 * `/acme/api/...` برود نه `/api/...` — وگرنه به نسخه‌ی دیگری روی همان دامنه
 * می‌رسید یا ۴۰۴ می‌گرفت.
 *
 * در توسعه هر دو خالی‌اند و `vite.config.ts` مسیر `/api` را proxy می‌کند.
 */
const baseURL = import.meta.env.VITE_API_BASE_URL ?? apiBase()

const createClient = (): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    // خط لوله‌ی مشاوره دو تماس LLM دارد و با تلاش مجدد، بدترین حالتش چند
    // دقیقه است. timeout سخاوتمندانه لازم است، وگرنه کلاینت قطع می‌کند در
    // حالی که سرور همچنان مشغول و در حال خرج کردن است.
    timeout: 300_000,
    headers: { Accept: 'application/json' },
  })

  instance.interceptors.request.use((config) => {
    const token = getToken()
    if (token !== '') {
      config.headers.Authorization = `Bearer ${token}`
    }
    /*
     * مسیر عمومی نسخه‌ی مشتری، روی **هر** درخواست.
     *
     * سرور با همین هدر می‌فهمد این درخواست به کدام نسخه‌ی منتشرشده تعلق دارد و
     * نشست را با همان می‌سنجد. اینجا گذاشتنش عمدی است: اگر هر تماس خودش باید
     * پارامتر را می‌فرستاد، فراموش شدنش در یکی از گام‌های میانی مشاوره نشست را
     * نامعتبر می‌کرد.
     */
    const slug = slugFromPath()
    if (slug !== '') {
      config.headers['X-Assistant-Slug'] = slug
    }
    return config
  })

  instance.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      if (!axios.isAxiosError(error)) {
        throw new ApiError({ message: UNKNOWN_MESSAGE, status: 0 })
      }
      const mapped = toApiError(error)
      // توکن نامعتبر بی‌درنگ پاک می‌شود، وگرنه هر درخواست بعدی هم ۴۰۱
      // می‌گیرد و کاربر در حلقه‌ی خطا می‌ماند بدون آنکه دروازه‌ی ورود را
      // ببیند.
      if (mapped.isUnauthorized) {
        clearToken()
      }
      throw mapped
    },
  )

  return instance
}

export const api = createClient()

/** `GET` تایپ‌شده. پارامترها را `axios` سریالایز می‌کند. */
export const get = async <T>(
  url: string,
  params?: Record<string, string | number | boolean | undefined>,
): Promise<T> => {
  const response = await api.get<T>(url, params === undefined ? undefined : { params })
  return response.data
}

export const post = async <T>(url: string, body?: unknown): Promise<T> => {
  const response = await api.post<T>(url, body)
  return response.data
}

export const put = async <T>(url: string, body?: unknown): Promise<T> => {
  const response = await api.put<T>(url, body)
  return response.data
}

/**
 * `PATCH` برای به‌روزرسانی جزئی.
 *
 * جدا از `put` است چون معنایش متفاوت است: در ویزارد پردازش، فیلد نافرستاده یعنی
 * «دست نزن» و بدون این تفکیک، ثبت یک تصمیم ساده مقدارهای پیش‌پرشده را پاک می‌کرد.
 */
export const patch = async <T>(url: string, body?: unknown): Promise<T> => {
  const response = await api.patch<T>(url, body)
  return response.data
}

export const del = async <T>(url: string): Promise<T> => {
  const response = await api.delete<T>(url)
  return response.data
}

/**
 * بارگذاری فایل چندبخشی.
 *
 * `Content-Type` عمداً تنظیم **نمی‌شود**: مرورگر باید خودش `boundary` را اضافه
 * کند و تنظیم دستی آن، درخواست را برای سرور غیرقابل تجزیه می‌کند.
 */
export const upload = async <T>(
  url: string,
  file: File,
  fields: Record<string, string> = {},
): Promise<T> => {
  const form = new FormData()
  form.append('file', file)
  for (const [key, value] of Object.entries(fields)) {
    form.append(key, value)
  }
  const response = await api.post<T>(url, form)
  return response.data
}

/** دانلود فایل. `Blob` برمی‌گرداند تا نام فایل از هدر خوانده شود. */
export const download = async (
  url: string,
  params?: Record<string, string>,
): Promise<{ blob: Blob; filename: string }> => {
  const response = await api.get<Blob>(url, {
    responseType: 'blob',
    ...(params === undefined ? {} : { params }),
  })
  const disposition = String(response.headers['content-disposition'] ?? '')
  const match = /filename="?([^"]+)"?/.exec(disposition)
  return { blob: response.data, filename: match?.[1] ?? 'download' }
}
